import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { LangProvider } from "@/context/LangContext";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { LocationProvider } from "@/context/LocationContext";
import Header from "@/components/Header";
import BottomNav from "@/components/BottomNav";
import InstallPrompt from "@/components/InstallPrompt";
import ProtectedRoute from "@/components/ProtectedRoute";
import CustomerOnlyRoute from "@/components/CustomerOnlyRoute";
import AuthCallback from "@/components/AuthCallback";
import Home from "@/pages/Home";
import Restaurants from "@/pages/Restaurants";
import RestaurantDetail from "@/pages/RestaurantDetail";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import Login from "@/pages/Login";
import StaffLogin from "@/pages/StaffLogin";
import Signup from "@/pages/Signup";
import Profile from "@/pages/Profile";
import Orders from "@/pages/Orders";
import OrderTracking from "@/pages/OrderTracking";
import Success from "@/pages/Success";
import LocationPicker from "@/pages/LocationPicker";
import Admin from "@/pages/Admin";
import Driver from "@/pages/Driver";
import Restaurant from "@/pages/Restaurant";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import DataDeletion from "@/pages/DataDeletion";
import RestaurantsMapPage from "@/pages/RestaurantsMap";
import Wallet from "@/pages/Wallet";
import AccountDetails from "@/pages/AccountDetails";
import ContactUs from "@/pages/ContactUs";
import About from "@/pages/About";
import Partner from "@/pages/Partner";
import Notifications from "@/pages/Notifications";
import Favorites from "@/pages/Favorites";
import Coupons from "@/pages/Coupons";
import { NotificationsProvider } from "@/context/NotificationsContext";
import { FavoritesProvider } from "@/context/FavoritesContext";

function AppRouter() {
  const location = useLocation();
  // Handle OAuth callback synchronously, before any other route logic
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <div className="flex flex-col h-[100dvh] overflow-hidden">
      <Header />
      <main className="flex-1 overflow-y-auto overscroll-contain app-scroll">
        <Routes>
          <Route path="/" element={<CustomerOnlyRoute><Home /></CustomerOnlyRoute>} />
          <Route path="/restaurants" element={<CustomerOnlyRoute><Restaurants /></CustomerOnlyRoute>} />
          <Route path="/restaurant/:id" element={<CustomerOnlyRoute><RestaurantDetail /></CustomerOnlyRoute>} />
          <Route path="/cart" element={<CustomerOnlyRoute><Cart /></CustomerOnlyRoute>} />
          <Route path="/checkout" element={<CustomerOnlyRoute><Checkout /></CustomerOnlyRoute>} />
          <Route path="/login" element={<Login />} />
          <Route path="/staff-login" element={<StaffLogin />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/success" element={<Success />} />
          <Route path="/location" element={<CustomerOnlyRoute><LocationPicker /></CustomerOnlyRoute>} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/driver" element={<Driver />} />
          <Route path="/owner" element={<Restaurant />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/data-deletion" element={<DataDeletion />} />
          <Route path="/account-deletion" element={<DataDeletion />} />
          <Route path="/map" element={<CustomerOnlyRoute><RestaurantsMapPage /></CustomerOnlyRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/orders" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
          <Route path="/profile/details" element={<ProtectedRoute><AccountDetails /></ProtectedRoute>} />
          <Route path="/profile/wallet" element={<ProtectedRoute><Wallet /></ProtectedRoute>} />
          <Route path="/profile/coupons" element={<ProtectedRoute><Coupons /></ProtectedRoute>} />
          <Route path="/profile/favorites" element={<ProtectedRoute><Favorites /></ProtectedRoute>} />
          <Route path="/profile/contact" element={<ContactUs />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/profile/about" element={<About />} />
          <Route path="/about" element={<About />} />
          <Route path="/profile/partner" element={<Partner />} />
          <Route path="/partner" element={<Partner />} />
          <Route path="/order/:orderId" element={<ProtectedRoute><OrderTracking /></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
        </Routes>
        <footer className="mt-20 border-t border-[#EAEAEA] py-8 text-center text-sm text-[#6B6B6B] hidden md:block">
          © 2026 Jayek · All rights reserved.
        </footer>
      </main>
      <BottomNav />
      <InstallPrompt />
    </div>
  );
}

export default function App() {
  return (
    <div className="App bg-[#F5F5F5] h-full overflow-hidden">
      <BrowserRouter>
        <LangProvider>
          <AuthProvider>
            <LocationProvider>
              <CartProvider>
                <FavoritesProvider>
                  <NotificationsProvider>
                    <AppRouter />
                    <Toaster position="top-center" richColors />
                  </NotificationsProvider>
                </FavoritesProvider>
              </CartProvider>
            </LocationProvider>
          </AuthProvider>
        </LangProvider>
      </BrowserRouter>
    </div>
  );
}
